import React, { useCallback, useState } from 'react';

import { ADMIN_TYPE_ADMIN } from '@pkg/components/Sections/Groups/data/adminType';
import { InputRadioGroup, type InputRadioGroupProps } from '@pkg/components/Sections/Profile/Inputs';
import { useAppDispatch, useGetGroup, useUpdateGroup } from '@pkg/hooks';

import { type GroupPrivacyTypes } from '@app/client/common/structs/groups/social-groups';
import { type UpdateSocialGroup } from '@app/gen';
import { addNotification } from '@app/store/actions/notification';

import { InputRadioGroupWrapper, Section, Title } from '../styles';

import { useSettingsContent } from './useSettingsContent';

export function OwnerSettings() {
    const dispatch = useAppDispatch();

    const { privacy, permissions } = useSettingsContent();
    const { data: groupData } = useGetGroup();
    const { mutate: mutateUpdate } = useUpdateGroup();

    const [waitingPrivacy, setWaitingPrivacy] = useState(false);
    const [waitingPermissions, setWaitingPermissions] = useState(false);
    const selectedPermissionValue = groupData?.invite_permission || ADMIN_TYPE_ADMIN;

    const handleChangeAnyPrivacy: InputRadioGroupProps['onChangeAny'] = useCallback(
        (result) => {
            if (groupData?.id) {
                setWaitingPrivacy(true);

                mutateUpdate(
                    {
                        id: groupData.id,
                        value: {
                            privacy: result.value as GroupPrivacyTypes,
                        },
                    },
                    {
                        // Do we also want to dispatch a `onSuccess()` message?
                        onSettled() {
                            setWaitingPrivacy(false);
                        },
                        onError({ message }) {
                            dispatch(addNotification({ message }));
                        },
                    },
                );
            }
        },
        [dispatch, groupData, mutateUpdate],
    );

    const handleChangeAnyPermissions: InputRadioGroupProps['onChangeAny'] = useCallback(
        (result) => {
            if (groupData?.id) {
                setWaitingPermissions(true);

                mutateUpdate(
                    {
                        id: groupData.id,
                        value: {
                            invite_permission: result.value as UpdateSocialGroup['invite_permission'],
                        },
                    },
                    {
                        // Do we also want to dispatch a `onSuccess()` message?
                        onSettled() {
                            setWaitingPermissions(false);
                        },
                        onError({ message }) {
                            dispatch(addNotification({ message }));
                        },
                    },
                );
            }
        },
        [dispatch, groupData, mutateUpdate],
    );

    return (
        <>
            <Section>
                <Title>{privacy.title}</Title>
                <InputRadioGroupWrapper>
                    <InputRadioGroup
                        items={privacy.items}
                        groupName={privacy.id}
                        awaitingChange={waitingPrivacy}
                        selectedValue={groupData?.privacy}
                        onChangeAny={handleChangeAnyPrivacy}
                    />
                </InputRadioGroupWrapper>
            </Section>

            <Section>
                <Title>{permissions.title}</Title>
                <InputRadioGroupWrapper>
                    <InputRadioGroup
                        items={permissions.items}
                        groupName={permissions.id}
                        awaitingChange={waitingPermissions}
                        selectedValue={selectedPermissionValue}
                        onChangeAny={handleChangeAnyPermissions}
                    />
                </InputRadioGroupWrapper>
            </Section>
        </>
    );
}
